import React, { useState } from 'react'
import { Box, Typography, Button, Tooltip } from '@mui/material'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import { GenAIInstructionsSource } from '../../../../types/gotypes'
import useGenAIInstructionsChat from '../../../../hooks/useGenAIInstructionsChat'
import useAiAccess from '../../../../hooks/useAiAccess'
import { InstructionsField } from './InstructionsField'
import { SystemContextAccordion } from './SystemContextAccordion'
import { ScopedAgentChatDialog } from './ScopedAgentChatDialog'
import { GuidedToggleState } from './wizardHelpers'

export interface WizardStep2InstructionsProps {
  customer: string
  job: string
  /** Step 0 card title — context for the Path B chat. */
  title: string
  instructions: string
  onInstructionsChange: (v: string) => void
  mentionSources: string[]
  /** Step 1 selection (names + paths) — context for the Path B chat. */
  chatSources: GenAIInstructionsSource[]
  /** Structured guided-toggle state (#1047) — carried through so it survives
   * into the saved widget data and the toggles re-derive correctly the first
   * time the flat editor reopens this card. */
  guidedToggles: GuidedToggleState
  onGuidedTogglesChange: (v: GuidedToggleState) => void
}

/** Step 2 — analysis instructions: scaffold, @mention, guided toggles, system context (#970 Part A). */
export const WizardStep2Instructions: React.FC<WizardStep2InstructionsProps> = ({
  customer,
  job,
  title,
  instructions,
  onInstructionsChange,
  mentionSources,
  chatSources,
  guidedToggles,
  onGuidedTogglesChange,
}) => {
  const [agentChatOpen, setAgentChatOpen] = useState(false)

  // Path B "Help me write this" (#970/#1048, D4: direct-LLM). The hook owns
  // all networking; the dialog below is purely presentational.
  const agentChat = useGenAIInstructionsChat({
    customer,
    open: agentChatOpen,
    context: {
      job,
      title,
      sources: chatSources,
      instructions_draft: instructions,
    },
  })

  // AI access (#1537): "Help me write this" needs level 2. This step already
  // owns the chat hook, so it reads the level beside it (cached per customer).
  const aiAccess = useAiAccess(customer)
  const canHelpWrite = aiAccess.can('help_me_write')

  const handleUseInstructions = (proposed: string) => {
    onInstructionsChange(proposed)
    setAgentChatOpen(false)
  }

  return (
    // maxWidth 860, was 640 (#1176): the guided toggles now sit in one
    // horizontal row and need the room; 640 wrapped them straight back into
    // the stack this change removes.
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 860, mx: 'auto', width: '100%' }}>
      <Typography variant="h6">What would you like the AI to analyse or write about?</Typography>

      <InstructionsField
        label="Analysis instructions"
        value={instructions}
        onChange={onInstructionsChange}
        mentionSources={mentionSources}
        rows={7}
        helperText="Your selected tables are analysed automatically — use @ only to single one out."
        guidedToggles={guidedToggles}
        onGuidedTogglesChange={onGuidedTogglesChange}
      />

      <Tooltip describeChild title={canHelpWrite ? '' : aiAccess.refusalFor('help_me_write')}>
        <span style={{ alignSelf: 'flex-start' }}>
          <Button
            variant="outlined"
            color="info"
            startIcon={<SmartToyIcon />}
            onClick={() => setAgentChatOpen(true)}
            disabled={!canHelpWrite}
          >
            Help me write this
          </Button>
        </span>
      </Tooltip>
      <ScopedAgentChatDialog
        open={agentChatOpen}
        onClose={() => setAgentChatOpen(false)}
        messages={agentChat.messages}
        isLoading={agentChat.isLoading}
        error={agentChat.error}
        onSend={agentChat.send}
        proposedInstructions={agentChat.proposedInstructions}
        onUseInstructions={handleUseInstructions}
      />

      <SystemContextAccordion customer={customer} job={job} />
    </Box>
  )
}

export default WizardStep2Instructions
